// src/components/chat/SiteFavicon.tsx

/**
 * A web source's favicon in a fixed 16px slot, with a neutral globe when the
 * site has none or Google's favicon service has not fetched it yet (it can
 * miss on the first request for a site). The slot never collapses, so a list
 * of sources stays aligned whatever the icons do.
 */

import React from "react";
import { siteFaviconUrl } from "../../utils/webSources";

const SIZE = 16;

const FallbackGlobe: React.FC = () => (
  <svg
    width={SIZE}
    height={SIZE}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    data-testid="site-favicon-fallback"
  >
    <circle cx="12" cy="12" r="9" stroke="#9CA3AF" strokeWidth="1.8" />
    <path
      d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z"
      stroke="#9CA3AF"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

interface SiteFaviconProps {
  /** The site's domain, e.g. "worldcc.com" */
  domain: string;
  style?: React.CSSProperties;
}

export const SiteFavicon: React.FC<SiteFaviconProps> = ({ domain, style }) => {
  const src = siteFaviconUrl(domain);
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const failed = !src || failedSrc === src;

  return (
    <span
      className="site-favicon"
      style={{
        display: "inline-flex",
        width: `${SIZE}px`,
        height: `${SIZE}px`,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      {failed ? (
        <FallbackGlobe />
      ) : (
        <img
          src={src}
          alt=""
          width={SIZE}
          height={SIZE}
          style={{ borderRadius: "2px" }}
          onError={() => setFailedSrc(src)}
        />
      )}
    </span>
  );
};

export default SiteFavicon;
